import React, { useCallback, useEffect, useRef, useState } from 'react';
import WallCardSkeleton from './WallCardSkeleton';
import useMediaQuery from '../../utils/useMediaQuery';
import WallEventCard, { estimateCardHeight } from './WallEventCard';
import useMasonry from './useMasonry';

/**
 * A list of WallEventCards in masonry columns — the events wall's ALL EVENTS body, and the same
 * body on /events/all, so every browse surface shows the one card, laid out one way.
 *
 * Columns: two where they fit (each ≥ ~380 wide), one below 980 or in LIST. Each card goes into
 * the shortest column and stays there (useMasonry). Poster ratios arrive as images load and
 * shape both the cards and the masonry's estimates.
 *
 *   seed      { ratios, masonry } from a cache, so a back-nav rebuilds the same layout
 *   onLayout  (ratios, masonrySnapshot) after the layout settles, to cache it
 *   resetKey  starts the columns over (a filter change)
 */
const GAP = 28;

export default function WallEventList({
  events = [],
  viewMode = 'grid',
  resetKey = '',
  refreshing = false,
  fetching = false,
  onEventOpen = null,
  portalFor = null,
  seed = null,
  onLayout = null,
  empty = null,
}) {
  const narrow = useMediaQuery('(max-width: 979px)');

  const bodyRef = useRef(null);
  // Start from the viewport so the first paint already has the right column count.
  const [inner, setInner] = useState(() => (typeof window === 'undefined' ? 1224 : Math.min(window.innerWidth, 1280) - (window.innerWidth < 640 ? 32 : 56)));
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth) setInner(el.clientWidth); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const cols = (narrow || viewMode === 'list') ? 1 : Math.max(1, Math.min(2, Math.floor((inner + GAP) / 380)));
  const cardW = (inner - GAP * (cols - 1)) / cols;

  const [ratios, setRatios] = useState(() => (seed && seed.ratios) || {});
  const onRatio = useCallback((id, r) => setRatios((prev) => (prev[id] === r ? prev : { ...prev, [id]: r })), []);
  const ratiosRef = useRef(ratios);
  ratiosRef.current = ratios;
  const estimate = useCallback((e) => estimateCardHeight(e, ratiosRef.current[e.id], cardW), [cardW]);
  const { columns, colRef, snapshot } = useMasonry(events, cols, estimate, resetKey, GAP, seed ? seed.masonry : null);

  // Report the settled layout (never per frame).
  useEffect(() => {
    if (!onLayout || events.length === 0) return undefined;
    const t = setTimeout(() => onLayout(ratios, snapshot()), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratios, events, cols, resetKey]);

  return (
    <div ref={bodyRef}>
      {events.length === 0 && !refreshing && !fetching && empty}
      <div className="grid items-start gap-7" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }} data-testid="wall-columns" data-cols={cols}>
        {columns.map((list, c) => (
          <div key={c} ref={colRef(c)} className="flex min-w-0 flex-col gap-7">
            {list.map((e) => (
              <WallEventCard key={e.id} event={e} width={cardW} ratio={ratios[e.id]} onRatio={onRatio} onOpen={onEventOpen || undefined} portal={portalFor ? portalFor(e.id) : null} loading={refreshing} />
            ))}
            {refreshing && list.length === 0 && Array.from({ length: 2 }, (_, i) => <WallCardSkeleton key={`first-${i}`} width={cardW} />)}
            {!refreshing && fetching && Array.from({ length: 2 }, (_, i) => <WallCardSkeleton key={`next-${i}`} width={cardW} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
